import { useState } from "react";

import useContractDropzone from "@panel/shared/contract/hooks/use-contract-dropzone";
import useContractThumbnail from "@panel/shared/contract/hooks/use-contract-thumbnail";
import useContractUpload from "@panel/shared/contract/hooks/use-contract-upload";

/**
 * Upload, confirm, save — as one render-ready object.
 *
 * Picking a file uploads it and stops there; confirming is what saves it as
 * the brand's contract. Cancelling at the confirmation step leaves the object
 * in the bucket but attached to nothing, and the contract on record untouched.
 * @param options
 */
const useTemplateUpload = ({ brandName, hasTemplate, saving, saveTemplate, onSaved }) => {
    const { uploading, uploadFile } = useContractUpload();
    // The uploaded-but-not-yet-saved file. Its presence is what opens the
    // confirmation dialog, and dropping it is what cancelling means.
    const [staged, setStaged] = useState(null);

    const handleFile = async (file) => {
        const uploaded = await uploadFile(file);
        if (uploaded) setStaged({ ...uploaded, file });
    };

    // One picker serves both the first upload and Replace.
    const dropzone = useContractDropzone({ onFile: handleFile, disabled: uploading });
    const thumbnail = useContractThumbnail({ file: staged?.file, active: Boolean(staged) });

    const handleConfirm = async () => {
        const saved = await saveTemplate(staged);
        if (!saved) return;

        setStaged(null);
        onSaved?.();
    };

    return {
        uploading,
        openPicker  : dropzone.openPicker,
        inputProps  : dropzone.inputProps,
        confirmModal: {
            open         : Boolean(staged),
            staged,
            thumbnail,
            targetName   : brandName,
            isReplacement: hasTemplate,
            saving,
            onConfirm    : handleConfirm,
            onCancel     : () => setStaged(null)
        }
    };
};

export default useTemplateUpload;
